import { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// A cheat sheet for the search box. Clicking a row adds that example to the
// box, so people learn the syntax by using it. Keep the list in step with
// OPERATOR_KEYS in lib/search-query.ts.

interface Example {
  text: string;
  what: string;
}

const SECTIONS: Array<{ title: string; items: Example[] }> = [
  {
    title: 'Words',
    items: [
      { text: 'invo', what: 'Partial words work: finds invoice, invoices' },
      { text: '"password reset"', what: 'An exact phrase' },
      { text: '-cancelled', what: 'Leave out tickets that mention a word' },
      { text: 'refund OR credit', what: 'Either word (OR in capitals)' },
      { text: '#2b89fbd1', what: 'A ticket ID (first 8 characters)' },
    ],
  },
  {
    title: 'Filters',
    items: [
      { text: 'from:liz@acme.com', what: 'Sender email or name contains' },
      { text: 'to:billing@', what: 'Sent to or copied to someone' },
      { text: 'assignee:me', what: 'me, none, or part of a name' },
      { text: 'status:closed', what: 'open, new, progress, waiting, resolved, closed' },
      { text: 'priority:p1,p2', what: 'One or more priorities' },
      { text: 'category:billing', what: 'billing, technical, complaint, sales, faq, onboarding, other' },
      { text: 'has:attachment', what: 'Has a file attached' },
    ],
  },
  {
    title: 'Dates (Sydney time)',
    items: [
      { text: 'after:2026-09-01', what: 'Created on or after this day' },
      { text: 'before:2026-10-01', what: 'Created before this day' },
    ],
  },
  {
    title: 'Where to look',
    items: [
      { text: 'is:archived', what: 'Switch tab: open, mine, team, unassigned, watching, snoozed, archived, all' },
    ],
  },
];

export function SearchSyntaxHelp({ onInsert }: { onInsert: (text: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Search tips"
          title="Search tips"
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[calc(100vw-1.5rem)] p-3">
        <p className="mb-2 text-2xs text-muted-foreground">
          Search looks through subjects, senders, every message, internal notes, attachment names,
          To/CC and contact names. Click an example to add it.
        </p>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="mb-1 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.title}
              </div>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.text}>
                    <button
                      type="button"
                      onClick={() => {
                        onInsert(item.text);
                        setOpen(false);
                      }}
                      className="flex w-full cursor-pointer items-baseline gap-2 rounded px-1.5 py-1 text-left text-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-2xs">
                        {item.text}
                      </code>
                      <span className="text-muted-foreground">{item.what}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
